import { Link } from 'react-router';
import { BookOpen, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useSEO } from '../lib/seo';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';

const endpoints = [
  {
    method: 'GET',
    path: '/partner/v1/orgs/:orgId/analytics',
    query: 'days=7..90 (default 30)',
    auth: 'X-Partner-Key',
    note: 'Aggregate RTI-style metrics for dashboards.',
  },
  {
    method: 'GET',
    path: '/partner/v1/orgs/:orgId/webhook-deliveries',
    query: 'limit=1..50 (default 20)',
    auth: 'X-Partner-Key',
    note: 'Recent outbound workflow webhook POST outcomes (retries, HTTP status, errors) for integration monitoring.',
  },
  {
    method: 'GET',
    path: '/partner/v1/orgs/:orgId/webhook-dead-letters',
    query: 'limit=1..50 (default 20)',
    auth: 'X-Partner-Key',
    note: 'Failures after all retries (dead-letter queue); pair with alerting on non-empty responses.',
  },
] as const;

export default function EnterprisePartnerDocs() {
  useSEO({
    title: 'Enterprise Partner API Docs | TENSION for Work',
    description: 'Integration docs for partner analytics endpoint and onboarding requirements.',
    canonical: '/enterprise/partner-docs',
    ogType: 'website',
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <EnterpriseSalesNav variant="dark" />
      <div className="enterprise-public-shell mx-auto max-w-5xl space-y-6 px-4 py-10 md:py-14">
        <header className="relative overflow-hidden rounded-2xl border border-slate-600/80 bg-slate-900/90 p-6 shadow-xl shadow-black/40 md:p-8">
          <div
            className="pointer-events-none absolute inset-0 opacity-90"
            aria-hidden
            style={{
              background:
                'radial-gradient(120% 70% at 0% 0%, rgba(37, 99, 235, 0.2), transparent 50%), radial-gradient(90% 55% at 100% 0%, rgba(15, 23, 42, 0.6), transparent 44%)',
            }}
          />
          <div className="relative">
            <img
              src="/branding/tension-enterprise-logo.svg"
              alt="TENSION Enterprise"
              className="mb-4 h-auto w-full max-w-md rounded-xl border border-slate-600/80 bg-slate-950/80 p-2"
            />
            <p className="inline-flex rounded-full border border-violet-500/40 bg-violet-950/60 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-200">
              Partner API — technical
            </p>
            <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-50 md:text-4xl">
              Integration reference (enterprise edition)
            </h1>
            <p className="mt-3 max-w-3xl text-base leading-relaxed text-slate-200 md:text-lg">
              Contract-safe aggregate analytics for partners. Use this reference for integration onboarding; your TENSION contact provisions sandbox credentials and tenant setup.
            </p>
          </div>
        </header>

        {endpoints.map((endpoint) => (
          <Card
            key={endpoint.path}
            className="rounded-2xl border border-slate-600/80 bg-slate-900/85 p-6 shadow-lg"
          >
            <h2 className="text-lg font-semibold text-slate-50">Endpoint</h2>
            <div className="mt-3 space-y-2 text-sm text-slate-200">
              <p>
                <span className="font-medium text-slate-100">Method:</span> {endpoint.method}
              </p>
              <p>
                <span className="font-medium text-slate-100">Path:</span>{' '}
                <code className="rounded bg-slate-950 px-1.5 py-0.5 text-slate-100">{endpoint.path}</code>
              </p>
              <p>
                <span className="font-medium text-slate-100">Query:</span> {endpoint.query}
              </p>
              <p>
                <span className="font-medium text-slate-100">Auth:</span> {endpoint.auth}
              </p>
              <p className="text-slate-300">{endpoint.note}</p>
            </div>
          </Card>
        ))}

        <Card className="rounded-2xl border border-slate-600/80 bg-slate-900/85 p-6 shadow-lg">
          <h2 className="text-lg font-semibold text-slate-50">Sample requests</h2>
          <pre className="mt-3 overflow-x-auto rounded-lg border border-slate-700 bg-slate-950 p-4 text-xs text-slate-100">
{`curl -X GET "https://<project>.supabase.co/functions/v1/make-server-ba6cedc4/partner/v1/orgs/<orgId>/analytics?days=30" \\
  -H "X-Partner-Key: <partner_key>"

curl -X GET "https://<project>.supabase.co/functions/v1/make-server-ba6cedc4/partner/v1/orgs/<orgId>/webhook-deliveries?limit=20" \\
  -H "X-Partner-Key: <partner_key>"

curl -X GET "https://<project>.supabase.co/functions/v1/make-server-ba6cedc4/partner/v1/orgs/<orgId>/webhook-dead-letters?limit=20" \\
  -H "X-Partner-Key: <partner_key>"`}
          </pre>
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="rounded-2xl border border-slate-600/80 bg-slate-900/80 p-5 shadow-md">
            <BookOpen className="h-5 w-5 text-blue-300" aria-hidden />
            <p className="mt-2 text-sm leading-relaxed text-slate-200">Request sandbox org and credentials from the TENSION enterprise team.</p>
          </Card>
          <Card className="rounded-2xl border border-slate-600/80 bg-slate-900/80 p-5 shadow-md">
            <KeyRound className="h-5 w-5 text-blue-300" aria-hidden />
            <p className="mt-2 text-sm leading-relaxed text-slate-200">Rotate partner keys according to contract and incident policy requirements.</p>
          </Card>
          <Card className="rounded-2xl border border-slate-600/80 bg-slate-900/80 p-5 shadow-md">
            <ShieldCheck className="h-5 w-5 text-blue-300" aria-hidden />
            <p className="mt-2 text-sm leading-relaxed text-slate-200">Consume aggregate outputs only; no raw user-level exports are exposed.</p>
          </Card>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-600/70 bg-slate-900/70 px-4 py-3 text-sm text-slate-200">
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
          Enterprise integration pathway — no consumer app surfaces on this page.
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/enterprise/platform">
            <Button variant="outline" className="border-slate-500 bg-slate-800 text-slate-100 hover:bg-slate-700">
              Enterprise platform overview
            </Button>
          </Link>
          <Link to="/enterprise/pilot">
            <Button className="bg-blue-600 text-white hover:bg-blue-500">Pilot framework</Button>
          </Link>
          <Link to="/business-inquiries?inquiryType=enterprise">
            <Button variant="secondary" className="bg-slate-700 text-slate-100 hover:bg-slate-600">
              Contact sales
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
